import { ArdoqEvent, ClientRequest, EventType, UserEvent } from '../sync/types';
import { ardoqEventOperations } from '../sync/ardoqEventOperations';
import { ResourceType } from '@ardoq/api-types';
import {
  ChatMessageContentEvent,
  ChatMessageDoneEvent,
  ChatMessageErrorEvent,
  ChatMessageToolCallEvent,
  ChatMessageToolResultEvent,
  ChatSessionConcludedEvent,
  ChatSessionNotificationReadEvent,
  ChatSessionTitleGeneratedEvent,
  AgentPlanEvent,
} from './types';
import type {
  AgentInteractionRequested,
  AgentInteractionResolved,
} from '@ardoq/api';

const isWebSocketChatEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<unknown>> =>
  ardoqEventOperations.isOfResourceType(event, ResourceType.CHAT);

export const isChatMessageContentEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<ChatMessageContentEvent>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.MESSAGE_CONTENT;

export const isChatMessageDoneEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<ChatMessageDoneEvent>> =>
  isWebSocketChatEvent(event) && event['event-type'] === EventType.MESSAGE_DONE;

export const isChatMessageErrorEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<ChatMessageErrorEvent>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.MESSAGE_ERROR;

export const isChatMessageToolCallEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<ChatMessageToolCallEvent>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.MESSAGE_TOOL_CALL;

export const isChatMessageToolResultEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<ChatMessageToolResultEvent>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.MESSAGE_TOOL_RESULT;

export const isChatSessionTitleGeneratedEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<ChatSessionTitleGeneratedEvent>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.CHAT_SESSION_TITLE_GENERATED;

export const isChatSessionConcludedEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<ChatSessionConcludedEvent>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.CHAT_SESSION_CONCLUDED;

export const isChatSessionNotificationReadEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<ChatSessionNotificationReadEvent>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.CHAT_SESSION_NOTIFICATION_READ;

export const isChatInteractionRequestedEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<AgentInteractionRequested>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.AGENT_INTERACTION_REQUESTED;

export const isChatInteractionResolvedEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<AgentInteractionResolved>> =>
  isWebSocketChatEvent(event) &&
  event['event-type'] === EventType.AGENT_INTERACTION_RESOLVED;

const PLAN_EVENT_TYPES: EventType[] = [
  EventType.PLAN_PROPOSED,
  EventType.TASK_STARTED,
  EventType.TASK_FINISHED,
  EventType.EXECUTION_FAILED,
  EventType.ORCHESTRATION_CONCLUDED,
];

export const isChatPlanEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<AgentPlanEvent>> =>
  isWebSocketChatEvent(event) && PLAN_EVENT_TYPES.includes(event['event-type']);
